const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;

export const utcDateFormatTo12HrFormat = (utcFormatDate: string) => {
  const date = new Date(utcFormatDate);
  const formateddate = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace("at", "|");
  return formateddate;
};


/** Format date string to separate date and time display parts */
export const formatDate = (dateStr: string): { datePart: string; timePart: string } => {
    try {
        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = {
            day: "2-digit",
            month: "long",
            year: "numeric",
            weekday: "long",
        };
        const datePart = date.toLocaleDateString("en-US", options);
        const timePart = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        return { datePart, timePart };
    } catch {
        return { datePart: dateStr, timePart: "" };
    }
};

/**
 * Compact date+time with timezone for space-constrained labels — e.g.
 * "Jul 11, 2026, 9:59 PM PDT". Uses short month so it fits on a single line
 * on narrow (phone) screens. Rendered in the viewer's local zone.
 */
export const formatDateTimeWithZone = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
};

/**
 * Backend inspection timestamps (createdAt) are stored as ISO-8601 UTC with
 * microseconds — e.g. "2026-01-02T11:30:00.000000Z". The history query compares
 * these as a lexicographic string range, so a calendar date must be expanded to a
 * full timestamp at the correct edge of the day.
 *
 * Crucially, the user picks a day in their LOCAL timezone, not UTC. A day edge is
 * therefore built in local time and then converted to the equivalent UTC instant,
 * so e.g. "start of Jan 2" in PST becomes "2026-01-02T08:00:00.000000Z" rather
 * than "2026-01-02T00:00:00Z". Appending a fixed "Z" suffix would skew the window
 * by the local UTC offset and drop/duplicate inspections near the day boundary.
 */

/**
 * Serializes a Date to the backend's ISO-8601 UTC format with microseconds
 * (6 fractional digits). JS only has millisecond precision, so the last three
 * digits are always zero — sufficient for the lexicographic range comparison.
 */
const toBackendUtcIso = (date: Date): string =>
  date.toISOString().replace("Z", "000Z"); // ".sssZ" -> ".sss000Z" (ms -> µs)

/**
 * Expands a local-timezone calendar date (YYYY-MM-DD) to the inclusive lower
 * bound of that day (local 00:00:00.000) as a backend-format UTC timestamp.
 * Returns undefined for an empty/missing date so callers can omit the bound.
 */
export const toStartOfDayIso = (date: string | undefined): string | undefined => {
  if (!date) return undefined;
  const [year, month, day] = date.split("-").map(Number);
  // Local midnight at the start of the picked day.
  return toBackendUtcIso(new Date(year, month - 1, day, 0, 0, 0, 0));
};

/**
 * Expands a local-timezone calendar date (YYYY-MM-DD) to the inclusive upper
 * bound of that day (local 23:59:59.999) as a backend-format UTC timestamp, so an
 * inspection created any time on the end date is included. Returns undefined for
 * an empty/missing date.
 */
export const toEndOfDayIso = (date: string | undefined): string | undefined => {
  if (!date) return undefined;
  const [year, month, day] = date.split("-").map(Number);
  // Local last instant of the picked day.
  return toBackendUtcIso(new Date(year, month - 1, day, 23, 59, 59, 999));
};

/**
 * Returns the calendar date (YYYY-MM-DD, local time) `monthsAgo` months before
 * `from` (defaults to today). Used to seed the default history date range.
 */
export const dateMonthsAgo = (monthsAgo: number, from: Date = new Date()): string => {
  const d = new Date(from);
  d.setMonth(d.getMonth() - monthsAgo);
  return toDateInputValue(d);
};

/** Formats a Date into a YYYY-MM-DD calendar date string in local time. */
export const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Formats a date string to short date — e.g. "Mar 25" */
export const formatShortDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

/** Formats a date string to time — e.g. "02:30 PM" */
export const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

/**
 * Formats the time remaining until `expiresAt` (Unix epoch SECONDS) relative to
 * `now` (epoch ms), with hours/minutes/seconds granularity. Leading zero units
 * are dropped, but seconds are always shown (it's the ticking unit). Returns
 * null when the remaining time is <= 0 (expired) or the value is invalid, so
 * callers can render an expired state.
 *
 * Examples: "2 hours 15 minutes 30 seconds", "15 minutes 3 seconds",
 * "42 seconds".
 */
export const formatTimeRemaining = (expiresAt: number, now: number): string | null => {
  if (!Number.isFinite(expiresAt)) return null;
  const endMs = expiresAt * MS_PER_SECOND; // epoch seconds -> ms
  const diffMs = endMs - now;
  if (diffMs <= 0) return null;
  const totalSeconds = Math.floor(diffMs / MS_PER_SECOND);
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  }
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  }
  parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
  return parts.join(" ");
};
